import { normalizeSectionId } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';

const CARD_SELECTOR = ':scope > [data-product-id][ref="cards[]"]';

/** Blue Zone owns display batches; ResultsList still appends and validates product cards. */
class BzCollectionPagination extends HTMLElement {
  #media = window.matchMedia('(min-width: 750px)');
  #observer;
  #scrollObserver;
  #sentinel;
  #request;
  #grid;
  #firstCard;
  #limit = 0;
  #manualThreshold = 0;
  #failed = false;
  #resetPending = false;
  #appendPage;
  #originalResolve;
  #list;

  async connectedCallback() {
    await customElements.whenDefined('results-list');
    if (!this.isConnected || this.#list) return;

    this.#list = this.closest('results-list');
    if (!this.#list) return;
    this.#originalResolve = this.#list.resolvePage;
    // Permit native rendering only for a page we have fetched and validated below.
    // Returning an out-of-range page also prevents speculative native fetches during a batch.
    this.#list.resolvePage = this.resolveNativePage;
    this.addEventListener('click', this.handleClick);
    this.#media.addEventListener('change', this.updateView);
    document.addEventListener(ThemeEvents.FilterUpdate, this.handleFilterUpdate);
    this.#observer = new MutationObserver(this.handleMutations);
    this.#observer.observe(this.#list, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-last-page', 'data-pdp-visibility-pending', 'data-isbn-visibility-hidden', 'hidden'],
    });
    this.#scrollObserver = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.target === this.#sentinel && entry.isIntersecting)) {
        this.loadBatch();
      }
    }, { rootMargin: '200px 0px' });
    this.resetGrid();
  }

  disconnectedCallback() {
    this.#request?.abort();
    this.#request = undefined;
    this.#observer?.disconnect();
    this.#scrollObserver?.disconnect();
    this.#sentinel = undefined;
    this.removeEventListener('click', this.handleClick);
    this.#media.removeEventListener('change', this.updateView);
    document.removeEventListener(ThemeEvents.FilterUpdate, this.handleFilterUpdate);
    if (this.#list?.resolvePage === this.resolveNativePage) this.#list.resolvePage = this.#originalResolve;
    this.#list = undefined;
  }

  resolveNativePage = (type) => {
    const page = this.#originalResolve.call(this.#list, type);
    if (type === 'next' && page?.page === this.#appendPage) return page;
    return { page: 0, url: new URL(window.location.href) };
  };

  nextPage() {
    return this.#originalResolve?.call(this.#list, 'next');
  }

  get batchSize() {
    return Number(this.dataset.batchSize) || 24;
  }

  get featuredCount() {
    return this.#media.matches && this.#grid?.querySelector(':scope > [data-bz-plp-featured-card]:not(.bz-plp-featured-card--hide-desktop)') ? 1 : 0;
  }

  get manualInterval() {
    return this.batchSize * 2;
  }

  get productLimit() {
    return this.#limit - this.featuredCount;
  }

  cards() {
    return Array.from(this.#grid?.querySelectorAll(CARD_SELECTOR) ?? []);
  }

  eligibleCards() {
    return this.cards().filter((card) =>
      !card.hidden && card.dataset.isbnVisibilityHidden !== 'true' && card.dataset.pdpVisibilityPending !== 'true'
    );
  }

  resetGrid() {
    this.#request?.abort();
    this.#request = undefined;
    this.#appendPage = undefined;
    this.#resetPending = false;
    this.#grid = this.#list.querySelector('[ref="grid"]');
    this.#firstCard = this.cards()[0];
    // Limits count visible grid items, including the desktop merchandising card.
    this.#limit = this.batchSize;
    this.#manualThreshold = this.manualInterval;
    this.#failed = false;
    this.#grid?.setAttribute('data-bz-batches-ready', '');
    this.updateView();
  }

  handleFilterUpdate = () => {
    this.#request?.abort();
    this.#resetPending = true;
    this.updateView();
  };

  handleMutations = (mutations) => {
    const grid = this.#list?.querySelector('[ref="grid"]');
    if (this.#request && grid === this.#grid && !this.#firstCard) this.#firstCard = this.cards()[0];
    const replaced = grid !== this.#grid || grid?.querySelector(CARD_SELECTOR) !== this.#firstCard;
    const gridChanged = mutations.some((mutation) => mutation.target === grid);
    if (replaced || (this.#resetPending && gridChanged)) {
      this.resetGrid();
    } else if (!this.#request) {
      this.updateView();
    }
  };

  updateView = () => {
    if (!this.#grid) return;
    const cards = this.eligibleCards();
    const deferred = new Set(cards.slice(this.productLimit));
    for (const card of this.cards()) card.toggleAttribute('data-bz-deferred', deferred.has(card));

    const moreAvailable = Boolean(this.nextPage()) || deferred.size > 0;
    const visibleItems = Math.min(cards.length, this.productLimit) + this.featuredCount;
    const atThreshold = visibleItems >= this.#manualThreshold;
    const wrapper = this.querySelector('[data-bz-load-more-wrapper]');
    const button = this.querySelector('[data-bz-load-more]');
    const busy = Boolean(this.#request);
    const showButton = !this.#resetPending && (atThreshold || this.#failed) && moreAvailable;
    if (wrapper && wrapper.hidden !== !showButton) wrapper.hidden = !showButton;
    if (button) {
      button.disabled = busy || !moreAvailable;
      button.setAttribute('aria-disabled', String(button.disabled));
      button.setAttribute('aria-busy', String(busy));
    }
    const error = this.querySelector('[data-bz-load-error]');
    if (error && error.hidden === this.#failed) error.hidden = !this.#failed;
    this.setAttribute('aria-busy', String(busy));

    // The sentinel is independent of the button, which is hidden between gates.
    const sentinel = !busy && !this.#failed && !this.#resetPending && !atThreshold && moreAvailable
      ? this.querySelector('[data-bz-load-sentinel]')
      : undefined;
    if (sentinel !== this.#sentinel) {
      this.#scrollObserver?.disconnect();
      this.#sentinel = sentinel;
      if (sentinel) this.#scrollObserver?.observe(sentinel);
    }
  };

  handleClick = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('[data-bz-load-more]')) return;
    event.preventDefault();
    this.loadBatch();
  };

  async loadBatch() {
    if (this.#request || this.#resetPending || !this.#grid) return;
    const request = new AbortController();
    this.#request = request;
    // Refill the current batch first if visibility checks or a resize left a gap.
    const target = this.eligibleCards().length < this.productLimit ? this.#limit : this.#limit + this.batchSize;
    this.#failed = false;
    this.updateView();

    try {
      while (!request.signal.aborted && this.#request === request && this.eligibleCards().length < target - this.featuredCount) {
        const next = this.nextPage();
        if (!next) break;
        await this.fetchAndAppend(next, request);
      }
      if (request.signal.aborted || this.#request !== request) return;

      this.#limit = target;
      // Only a successful click past a gate unlocks the next group of 48 items.
      if (target > this.#manualThreshold) this.#manualThreshold += this.manualInterval;
    } catch (failure) {
      if (!request.signal.aborted && this.#request === request) {
        this.#failed = true;
        console.error('Unable to load the next Blue Zone product batch.', failure);
      }
    } finally {
      if (this.#request === request) {
        this.#request = undefined;
        for (const card of this.cards()) card.removeAttribute('data-bz-batch-staged');
        this.updateView();
      }
    }
  }

  async fetchAndAppend(page, request) {
    const url = new URL(page.url);
    url.searchParams.set('section_id', normalizeSectionId(this.#list.getAttribute('section-id')));
    const response = await fetch(url, { signal: request.signal });
    if (!response.ok) throw new Error(`Section request failed (${response.status}).`);
    const html = await response.text();
    if (request.signal.aborted || this.#request !== request) throw new DOMException('Batch reset', 'AbortError');

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const grid = doc.querySelector('.bz-main-collection [ref="grid"]');
    if (!grid || Number(grid.dataset.bzPage) !== page.page) throw new Error('Unexpected product page response.');
    // Keep uncommitted cards out of both viewports until the batch is ready.
    grid.querySelectorAll(CARD_SELECTOR).forEach((card) => card.setAttribute('data-bz-batch-staged', ''));
    this.#list.pages.set(page.page, doc.documentElement.outerHTML);
    this.#appendPage = page.page;
    try {
      // Reuse native append, page cursor advancement and adoption/ISBN visibility checks.
      await this.#list.loadNextPage();
    } finally {
      if (this.#request === request) this.#appendPage = undefined;
    }
  }
}

if (!customElements.get('bz-collection-pagination')) {
  customElements.define('bz-collection-pagination', BzCollectionPagination);
}
